import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { useLang, tr } from "../contexts/LangContext";
import { withLang } from "../components/SeoHead";
import api from "../lib/api";
import { Images, Calendar } from "lucide-react";

export default function Galerie() {
  const { t, lang } = useLang();
  const [albums, setAlbums] = useState([]);
  const [year, setYear] = useState("all");

  useEffect(() => {
    api.get("/gallery/albums").then((r) => setAlbums(r.data || [])).catch(() => {});
  }, []);

  const years = useMemo(() => {
    const set = new Set(albums.map((a) => a.year));
    return ["all", ...Array.from(set).sort((a, b) => b - a)];
  }, [albums]);

  const filtered = year === "all" ? albums : albums.filter((a) => a.year === year);
  const backend = process.env.REACT_APP_BACKEND_URL;
  const fullUrl = (u) => (u && u.startsWith("/uploads/") ? `${backend}/api${u}` : u);

  return (
    <div data-testid="gallery-page">
      <PageHeader eyebrow={t.gallery.eyebrow} title={t.gallery.title} lead={t.gallery.lead} />

      <section className="pb-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          {/* Year tabs */}
          <div className="flex flex-wrap gap-1 border-b border-slate-200 mb-10" data-testid="gallery-years">
            {years.map((y) => (
              <button
                key={y}
                onClick={() => setYear(y)}
                className={`px-5 py-3 text-xs uppercase tracking-[0.2em] border-b-2 transition-colors ${year === y ? "border-club-red text-club-red font-semibold" : "border-transparent text-slate-500 hover:text-navy"}`}
                data-testid={`gallery-year-${y}`}
              >
                {y === "all" ? (t.gallery.allYears || tr("Toutes les années")) : y}
              </button>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center text-slate-500 py-12 bg-sand border border-dashed border-slate-300" data-testid="gallery-empty">
              {t.gallery.empty || tr("Aucun album publié pour le moment.")}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((a) => (
              <Link
                key={a.id}
                to={withLang(lang, `/galerie/${a.slug}`)}
                className="group block bg-white border border-slate-200 hover:border-club-red transition-colors"
                data-testid={`gallery-album-${a.slug}`}
              >
                <div className="aspect-[4/3] bg-sand overflow-hidden flex items-center justify-center">
                  {a.cover_image ? (
                    <img src={fullUrl(a.cover_image)} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  ) : (
                    <Images className="w-12 h-12 text-slate-300" />
                  )}
                </div>
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-club-yellow-dark font-semibold mb-2 flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> {a.year}
                    {a.date && (
                      <span className="ml-2 text-slate-400 normal-case tracking-normal">
                        {new Date(a.date).toLocaleDateString(lang === "en" ? "en-GB" : lang, { day: "2-digit", month: "short" })}
                      </span>
                    )}
                  </div>
                  <h3 className="font-serif text-xl text-navy mb-2 group-hover:text-club-red">{a.title}</h3>
                  <div className="text-[11px] uppercase tracking-widest text-slate-500">
                    {a.photo_count} photo{a.photo_count > 1 ? "s" : ""}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
